import React from 'react';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { Box } from '@waglabs/box-next';
import { Grid } from '@waglabs/grid';
import { Button } from '@waglabs/button-next';
import { TextField } from '@waglabs/textfield';
import { Typography } from '@waglabs/typography';
import { useToggle } from '@waglabs/use-toggle';

// TODO - Move to wag-react
import LoadingBoneModal from '../../../containers/WagSignUp/components/LoadingBoneOverlay';

import { EyeIcon } from '../../../icons';

import { passwordSchema } from '../../../utils/validation-schemas/password';
import { getFormikFieldHelper } from '../../../utils/getFormikHelper';

import { DATA_TEST_IDS } from '../../../constants';

export type FormValues = {
  password: string;
  verifyPassword: string;
};

export type PasswordResetFormProps = {
  error?: string;
  isLoading?: boolean;
  onSubmit: (values: FormValues) => void;
};

const FormFields = {
  Password: 'password',
  VerifyPassword: 'verifyPassword',
} as const;

const validationSchema = Yup.object({
  [FormFields.Password]: passwordSchema.default(''),
  [FormFields.VerifyPassword]: Yup.string()
    .oneOf([Yup.ref(FormFields.Password), null], 'passwords must match')
    .default(''),
});

const PasswordResetForm = ({
  onSubmit,
  isLoading = false,
  error,
}: PasswordResetFormProps) => {
  const {
    on: isPasswordVisible,
    toggle: togglePasswordVisiblity,
  } = useToggle();

  const {
    on: isVerifyPasswordVisible,
    toggle: toggleVerifyPasswordVisiblity,
  } = useToggle();

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    touched,
    isValid,
    values,
  } = useFormik({
    isInitialValid: false,
    initialValues: validationSchema.cast(undefined),
    validationSchema,
    onSubmit,
  });

  const formikFieldHelper = getFormikFieldHelper({
    errors,
    touched,
  });

  return (
    <form
      onSubmit={handleSubmit}
    >
      {isLoading && (
        <LoadingBoneModal />
      )}
      <Grid
        gridGap={[2, 3]}
      >
        <TextField
          label="Password"
          name={FormFields.Password}
          variant="outlined"
          inputProps={{
            fontFamily: 'muli',
            endAdornment: () => (
              <EyeIcon
                onClick={togglePasswordVisiblity}
                color="#727272"
                cursor="pointer"
              />
            ),
          }}
          containerProps={{
            backgroundColor: 'white',
            // Due to nature of this page having a grey background,
            // we need to update the backgroundColor. Having said that,
            // the Textfield component thought the user is manually handling it
            // that's why we have to manually set the borderColor
            // TODO - Update this on @waglabs/textfield
            borderColor: formikFieldHelper(FormFields.Password).hasError
              ? 'red'
              : 'white',
          }}
          type={isPasswordVisible ? 'text' : 'password'}
          value={values[FormFields.Password]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={formikFieldHelper(FormFields.Password).hasError}
          helperText={formikFieldHelper(FormFields.Password).errorMessage}
          data-testId={DATA_TEST_IDS.ResetPassword.ResetPasswordForm.Password}
          fullWidth
        />

        <TextField
          label="Confirm Password"
          name={FormFields.VerifyPassword}
          inputProps={{
            fontFamily: 'muli',
            endAdornment: () => (
              <EyeIcon
                onClick={toggleVerifyPasswordVisiblity}
                color="#727272"
                cursor="pointer"
              />
            ),
          }}
          containerProps={{
            backgroundColor: 'white',
            // Due to nature of this page having a grey background,
            // we need to update the backgroundColor. Having said that,
            // the Textfield component thought the user is manually handling it
            // that's why we have to manually set the borderColor
            // TODO - Update this on @waglabs/textfield
            borderColor: formikFieldHelper(FormFields.VerifyPassword).hasError
              ? 'red'
              : 'white',
          }}
          type={isVerifyPasswordVisible ? 'text' : 'password'}
          value={values[FormFields.VerifyPassword]}
          onChange={handleChange}
          onBlur={handleBlur}
          error={formikFieldHelper(FormFields.VerifyPassword).hasError}
          helperText={formikFieldHelper(FormFields.VerifyPassword).errorMessage}
          data-testId={DATA_TEST_IDS.ResetPassword.ResetPasswordForm.VerifyPassword}
          fullWidth
        />

        <Box>
          {error && (
            <Typography
              variant="legal"
              color="red"
              px={['unset', '48px']}
              data-testId={DATA_TEST_IDS.ResetPassword.ResetPasswordForm.ErrorText}
            >
              {error}
            </Typography>
          )}
          <Button
            type="submit"
            disabled={!isValid || isLoading}
            data-testId={DATA_TEST_IDS.ResetPassword.ResetPasswordForm.SubmitBtn}
            variant="contained"
          >
            Change Password
          </Button>
        </Box>
      </Grid>
    </form>
  );
};

export default PasswordResetForm;
